import type { RpcErrorCode, RpcRequest, RpcResponse, ScannerRpcMethod } from "../types";
import { SCANNER_PROTOCOL_VERSION } from "../types";
import { backoffDelayMs } from "./backoff";

export const UNSUPPORTED_PROTOCOL_CLOSE_CODE = 4001;
export const SESSION_REPLACED_CLOSE_CODE = 4002;
export const AUTH_FAILED_CLOSE_CODE = 4003;
const RPC_TIMEOUT_MS = 5_000;

export type GatewayConnectionState = "connecting" | "open" | "reconnecting" | "closed";

export interface ClientAuthParams {
  startToken: string;
  initData: string;
}

export type ClientAuth = string | ClientAuthParams;

export interface ClientCallbacks {
  onStateChange: (state: GatewayConnectionState) => void;
  onAuthSuccess?: (session?: unknown) => void;
  onAuthError?: (error: { code: string; message: string }) => void;
}

export interface RpcClientError {
  code: RpcErrorCode | "TRANSPORT_CLOSED";
  message: string;
}

interface PendingCall {
  resolve: (result: unknown) => void;
  reject: (error: RpcClientError) => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Normalizes a raw gateway URL into a WebSocket base URL.
 * Automatically converts https:// -> wss:// and http:// -> ws://.
 * Defaults to 'wss://gw.sod.ee' if not provided or empty.
 */
export function normalizeGatewayWsUrl(rawUrl?: string): string {
  if (!rawUrl) {
    return "wss://gw.sod.ee";
  }
  let trimmed = rawUrl.trim();
  if (!trimmed) {
    return "wss://gw.sod.ee";
  }

  // Strip trailing slashes
  trimmed = trimmed.replace(/\/+$/, "");

  // If path was already passed with /client, strip it so `${url}/client` doesn't double-append
  trimmed = trimmed.replace(/\/client$/, "");

  if (trimmed.startsWith("https://")) {
    return `wss://${trimmed.slice("https://".length)}`;
  }
  if (trimmed.startsWith("http://")) {
    return `ws://${trimmed.slice("http://".length)}`;
  }
  if (trimmed.startsWith("wss://") || trimmed.startsWith("ws://")) {
    return trimmed;
  }

  // Scheme-less host: default to secure WebSocket
  return `wss://${trimmed}`;
}

/**
 * Builds the full WebSocket connection URL for the /client endpoint including auth query parameters (startToken + initData or token).
 */
export function buildClientWebSocketUrl(gatewayUrl: string | undefined, auth: ClientAuth): string {
  const wsBase = normalizeGatewayWsUrl(gatewayUrl);
  if (typeof auth === "string") {
    return `${wsBase}/client?token=${encodeURIComponent(auth)}`;
  }
  const params = new URLSearchParams({
    startToken: auth.startToken,
    initData: auth.initData,
  });
  return `${wsBase}/client?${params.toString()}`;
}

/**
 * WebSocket client for the Session Gateway's `/client` endpoint (spec §9, §38):
 * connects with direct startToken+initData handshake or token, performs the protocol
 * handshake, correlates RPC request/response by id, and reconnects with
 * backoff on any non-terminal disconnect. The camera/scanner keeps running
 * across a reconnect — only RPC calls pause (spec §38).
 */
export class ScannerGatewayClient {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private stopped = false;
  private readonly pending = new Map<string, PendingCall>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly wsUrl: string;
  private readonly onStateChange: (state: GatewayConnectionState) => void;
  private readonly onAuthSuccess?: (session?: unknown) => void;
  private readonly onAuthError?: (error: { code: string; message: string }) => void;

  constructor(
    gatewayUrl: string | undefined,
    auth: ClientAuth,
    callbacksOrStateChange: ((state: GatewayConnectionState) => void) | ClientCallbacks,
    onAuthError?: (error: { code: string; message: string }) => void,
    onAuthSuccess?: (session?: unknown) => void,
  ) {
    this.wsUrl = buildClientWebSocketUrl(gatewayUrl, auth);
    if (typeof callbacksOrStateChange === "function") {
      this.onStateChange = callbacksOrStateChange;
      this.onAuthError = onAuthError;
      this.onAuthSuccess = onAuthSuccess;
    } else {
      this.onStateChange = callbacksOrStateChange.onStateChange;
      this.onAuthError = callbacksOrStateChange.onAuthError;
      this.onAuthSuccess = callbacksOrStateChange.onAuthSuccess;
    }
  }

  getWebSocketUrl(): string {
    return this.wsUrl;
  }

  connect(): void {
    this.stopped = false;
    this.open();
  }

  /** Permanently stops the client — no further reconnect attempts (e.g. session expired). */
  stop(): void {
    this.stopped = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.failAllPending({ code: "TRANSPORT_CLOSED", message: "Client stopped" });
    this.ws?.close();
    this.ws = null;
  }

  /**
   * Resume after Telegram hides the Mini App: iOS often drops the socket without
   * remounting React, so the reconnect timer never runs. No-ops if stop() was
   * called (unmount or terminal auth failure).
   */
  resume(): void {
    if (this.stopped) return;
    const state = this.ws?.readyState;
    if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.attempt = 0;
    this.open();
  }

  request<TResult = unknown>(method: ScannerRpcMethod, params: unknown): Promise<TResult> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return Promise.reject({ code: "TRANSPORT_CLOSED", message: "Not connected" } satisfies RpcClientError);
    }

    const id = crypto.randomUUID();
    const request: RpcRequest = { id, method, params };

    return new Promise<TResult>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject({ code: "TIMEOUT", message: "Gateway did not respond in time" } satisfies RpcClientError);
      }, RPC_TIMEOUT_MS);

      this.pending.set(id, { resolve: resolve as (result: unknown) => void, reject, timer });
      this.ws!.send(JSON.stringify(request));
    });
  }

  private open(): void {
    this.onStateChange(this.attempt === 0 ? "connecting" : "reconnecting");
    const ws = new WebSocket(this.wsUrl);
    this.ws = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "hello", protocol: SCANNER_PROTOCOL_VERSION }));
    };

    ws.onmessage = (event) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(event.data as string);
      } catch {
        return;
      }
      this.handleMessage(parsed);
    };

    ws.onclose = (event) => {
      // A reconnect opens a second socket; the gateway then closes the old one
      // with 4002. That stale close must not null out the live socket or mark
      // the client terminal — otherwise the UI stays Offline forever.
      if (this.ws !== ws) {
        return;
      }
      this.ws = null;
      this.failAllPending({ code: "TRANSPORT_CLOSED", message: "Connection closed" });

      if (
        this.stopped ||
        event.code === UNSUPPORTED_PROTOCOL_CLOSE_CODE ||
        event.code === AUTH_FAILED_CLOSE_CODE
      ) {
        if (event.code === AUTH_FAILED_CLOSE_CODE) {
          const reason = event.reason || "Authentication failed";
          this.onAuthError?.({ code: "AUTH_FAILED", message: reason });
        }
        this.onStateChange("closed");
        return;
      }

      this.onStateChange("reconnecting");
      const delay = backoffDelayMs(this.attempt);
      this.attempt += 1;
      this.reconnectTimer = setTimeout(() => this.open(), delay);
    };

    ws.onerror = () => {
      // 'close' always follows; reconnect scheduling happens there.
    };
  }

  private handleMessage(parsed: unknown): void {
    const type = (parsed as Record<string, unknown> | null)?.type;
    if (type === "hello") {
      this.attempt = 0;
      this.onStateChange("open");
      this.onAuthSuccess?.();
      return;
    }

    if (type === "auth_ok") {
      this.attempt = 0;
      this.onStateChange("open");
      this.onAuthSuccess?.((parsed as Record<string, unknown>).session);
      return;
    }

    if (type === "auth_error") {
      const err = (parsed as { error?: { code?: string; message?: string } })?.error ?? {
        code: "UNAUTHORIZED",
        message: "Authentication failed",
      };
      this.onAuthError?.({
        code: err.code || "UNAUTHORIZED",
        message: err.message || "Authentication failed",
      });
      this.stop();
      return;
    }

    const response = parsed as RpcResponse | null;
    if (!response || typeof response.id !== "string") return;

    const call = this.pending.get(response.id);
    if (!call) return;

    clearTimeout(call.timer);
    this.pending.delete(response.id);

    if ("error" in response && response.error) {
      call.reject(response.error);
    } else {
      call.resolve((response as { result: unknown }).result);
    }
  }

  private failAllPending(error: RpcClientError): void {
    for (const call of this.pending.values()) {
      clearTimeout(call.timer);
      call.reject(error);
    }
    this.pending.clear();
  }
}

import { getFriendlyRpcErrorMessage as getFriendlyRpcErrorMessageI18n, type Language } from "../i18n";

/**
 * Returns a user-friendly localized message for standard RPC error codes.
 */
export function getFriendlyRpcErrorMessage(
  code: string,
  langOrFallback?: Language | string,
  fallbackMessage?: string,
): string {
  return getFriendlyRpcErrorMessageI18n(code, langOrFallback, fallbackMessage);
}


